import clsx from "clsx";
import { FC, ReactNode, useState } from "react";
import { Asset } from "rujira.js";
import { Button, Icons, useTranslation } from "rujira.ui";
import {
  MobileCardToggle,
  useCardCollapse,
} from "../../../common/components/MobileCard";
import { PositionBorrowRowFragment$data } from "../__generated__/PositionBorrowRowFragment.graphql";
import { LiquidationPrice, LtvGraph } from "../Health";
import { MultiAsset } from "../MultiAsset";
import { PostitionRowDebt } from "../PositionAssets";
import { Cdp } from "../utils";

const CardStat: FC<{ label: string; children: ReactNode }> = ({
  label,
  children,
}) => (
  <div className="borrow-card__stat">
    <span className="mobile-card__label">{label}</span>
    <div className="borrow-card__stat-value">{children}</div>
  </div>
);

export const PositionCard: FC<{
  data: PositionBorrowRowFragment$data;
  p: Cdp<Asset>;
  onManage?: () => void;
  //Disables the see more / see less toggling
  embedded?: boolean;
  // The dialog graphs an oracle-adjusted LTV; the list graphs the stored one.
  ltv?: bigint;
}> = ({ data, p, onManage, embedded = false, ltv }) => {
  const { t } = useTranslation();
  const { collapsed, toggle } = useCardCollapse();
  const expanded = embedded || !collapsed;
  const Root = embedded ? "div" : "li";
  const [liquidationLabel, setLiquidationLabel] = useState(
    t("liquidationPrice")
  );
  const paidOff = p.debts[0].amount === 0n;

  return (
    <Root
      className={clsx("mobile-card borrow-card", {
        "mobile-card--embedded": embedded,
        "mobile-card--flat": embedded,
        "borrow-card--embedded": embedded,
      })}>
      <div className="flex ai-c gap-1 borrow-card__head">
        <div className="flex dir-c gap-0.5">
          <span className="mobile-card__label mb-0.5">{t("collateral")}</span>
          <MultiAsset collaterals={data.collaterals} />
        </div>
        <div className="ml-a">
          {paidOff ? (
            <span className="tag tag--borderless tag--md tag--teal mb-4">
              {t("paidOff")}
            </span>
          ) : (
            <div className="flex dir-c ai-c gap-0.5">
              <span className="mobile-card__label">{t("ltv")}</span>
              <LtvGraph
                ltv={ltv ?? BigInt(data.ltv)}
                size={2.75}
                decimals={0}
              />
            </div>
          )}
        </div>
      </div>

      {paidOff && expanded && !embedded && (
        <p className="mobile-card__panel borrow-card__note">
          {t("paidOffMessage")}
        </p>
      )}

      {!paidOff && (
        <>
          <div
            className={clsx("mobile-card__panel borrow-card__debt", {
              "mobile-card__panel--bare": !expanded,
            })}>
            <div className="flex dir-c gap-0.5">
              <span className="mobile-card__label">{t("borrowed")}</span>
              <div className="flex dir-c gap-1">
                {data.debts.map((x, idx) => (
                  <PostitionRowDebt key={idx} {...x} />
                ))}
              </div>
            </div>

            <div className="flex dir-c ai-e gap-0.5 text-right">
              <span className="mobile-card__label">{t("interestRate")}</span>
              <div className="tag tag--borderless tag--index mt-1">
                {(Number(p.debts[0].rate) / 1e10).toLocaleDecimal(2)}%
              </div>
            </div>
          </div>

          {expanded && (
            <div className="mobile-card__panel borrow-card__stats">
              <CardStat label={liquidationLabel}>
                <LiquidationPrice
                  p={p}
                  account={data}
                  onOrientationSet={(_, labelValue) =>
                    setLiquidationLabel(labelValue)
                  }
                />
              </CardStat>
            </div>
          )}
        </>
      )}

      {expanded && onManage && (
        <div className="mobile-card__footer">
          <Button
            className="button--sm button--outline button--grey button--icon-right w-full"
            onClick={onManage}
            label={t("manage")}>
            <Icons.ArrowUpRight />
          </Button>
        </div>
      )}

      {!embedded && (
        <MobileCardToggle collapsed={collapsed} onToggle={toggle} />
      )}
    </Root>
  );
};
